import React from 'react';
import { User } from '../types';
import { cn } from '../lib/utils';
import {
  Sparkles,
  Shield,
  Zap,
  Star,
  Gem
} from 'lucide-react';

interface UserTierBadgeProps {
  tier?: User['tier'];
  className?: string;
  variant?: 'pill' | 'outline' | 'solid' | 'minimal';
  cleanLabel?: boolean; // If true, removes "Plan" suffix
  compact?: boolean; // If true, uses abbreviated names
  showIcon?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const UserTierBadge: React.FC<UserTierBadgeProps> = ({ 
  tier = 'free', 
  className, 
  variant = 'pill',
  cleanLabel = true,
  compact = false,
  showIcon = false,
  size = 'md'
}) => {
  
  // Configuration for each tier
  const tierConfig: Record<string, { 
    label: string; 
    shortLabel: string; 
    colors: {
      solid: string;
      outline: string;
      pill: string;
      minimal: string;
      dot: string;
    };
    icon: React.ElementType;
  }> = {
    enterprise: {
      label: 'Enterprise',
      shortLabel: 'Ent',
      colors: {
        solid: 'bg-indigo-600 text-white border-transparent',
        outline: 'bg-transparent text-indigo-400 border-indigo-500/50',
        pill: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20',
        minimal: 'text-indigo-400',
        dot: 'bg-indigo-500'
      },
      icon: Shield
    },
    premium: {
      label: 'Premium',
      shortLabel: 'Prem',
      colors: {
        solid: 'bg-purple-600 text-white border-transparent',
        outline: 'bg-transparent text-purple-400 border-purple-500/50',
        pill: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
        minimal: 'text-purple-400',
        dot: 'bg-purple-500'
      },
      icon: Gem
    },
    paid: {
      label: 'Pro',
      shortLabel: 'Pro',
      colors: {
        solid: 'bg-blue-600 text-white border-transparent',
        outline: 'bg-transparent text-blue-400 border-blue-500/50',
        pill: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
        minimal: 'text-blue-400',
        dot: 'bg-blue-500'
      },
      icon: Zap
    },
    early_access: {
      label: 'Early Access',
      shortLabel: 'Early',
      colors: {
        solid: 'bg-amber-500 text-black border-transparent',
        outline: 'bg-transparent text-amber-400 border-amber-500/50',
        pill: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
        minimal: 'text-amber-400',
        dot: 'bg-amber-500'
      },
      icon: Star
    },
    free: {
      label: 'Free',
      shortLabel: 'Free',
      colors: {
        solid: 'bg-slate-600 text-white border-transparent',
        outline: 'bg-transparent text-slate-400 border-slate-500/50',
        pill: 'bg-slate-500/10 text-slate-400 border-slate-500/20',
        minimal: 'text-slate-400',
        dot: 'bg-slate-500'
      },
      icon: Sparkles
    }
  };

  const config = tierConfig[tier] || tierConfig.free;
  
  // Size classes
  const sizeClasses = {
    sm: 'text-[10px] px-1.5 py-0.5 h-5 gap-1',
    md: 'text-xs px-2.5 py-1 h-6 gap-1.5',
    lg: 'text-sm px-3 py-1.5 h-8 gap-2'
  };

  const Icon = config.icon;

  // Minimal variant just renders text with a colored dot
  if (variant === 'minimal') {
    return (
      <span className={cn("inline-flex items-center gap-1.5 font-medium", config.colors.minimal, className)}>
        <span className={cn("rounded-full", config.colors.dot, size === 'sm' ? 'w-1.5 h-1.5' : 'w-2 h-2')} />
        <span className="uppercase tracking-wide">
          {compact ? config.shortLabel : config.label}
          {!cleanLabel && ' Plan'}
        </span>
      </span>
    );
  }

  return (
    <span className={cn(
      "inline-flex items-center font-semibold rounded-full border transition-colors whitespace-nowrap",
      config.colors[variant],
      sizeClasses[size],
      className
    )}>
      {showIcon && <Icon className={cn(size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5')} />}
      
      {!showIcon && variant !== 'solid' && (
         <span className={cn("rounded-full", config.colors.dot, size === 'sm' ? 'w-1 h-1' : 'w-1.5 h-1.5')} />
      )}
      
      <span className={variant === 'solid' ? '' : 'uppercase tracking-wide'}>
        {compact ? config.shortLabel : config.label}
        {!cleanLabel && ' Plan'}
      </span>
    </span>
  );
};

export default UserTierBadge;
